import {
  Button,
  Text,
  TextSpinner,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { getEmployeeCountCopy, getVerbCountCopy } from 'modules/shift_management/lib/getDescription'
import type { ConflictsProps } from 'modules/shift_management/lib/types'

import useApplyTemplate from './useApplyTemplate'
import useReplaceShifts, { Props as ReplaceShiftsAttributes } from './useReplaceShifts'

interface Props {
  conflicts: ConflictsProps
  params: ReplaceShiftsAttributes['params']
  onClose: () => void
}

const OverlappingShiftsModal = ({ conflicts, params, onClose }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ shiftsRows }] = useShiftsRows()
  const [tooltip, toggleTooltip] = useToggle()
  const employeeIds = conflicts.map((conflict) => conflict.id)
  const employeeCount = employeeIds.length

  const shiftsRowsList = shiftsRows.filter((row) => employeeIds.includes(row.id))

  const shiftsIds = conflicts.flatMap((conflict) => conflict.shiftsIds)
  const restDaysIds = conflicts.flatMap((conflict) => conflict.restDaysIds)

  const { onReplaceShifts, submitting } = useReplaceShifts({
    params,
    shiftsIds,
    restDaysIds,
    from: 'modal',
  })

  const { onApplyTemplate, submittingApply } = useApplyTemplate({
    params,
    from: 'modal',
    onSuccess: () => onClose(),
  })

  const restDayTemplate = hasFeatureFlag('shift_management_rest_day')
  const title = restDayTemplate
    ? i18n.t('shift_management.conflict.modal_title_complete')
    : i18n.t('shift_management.conflict.modal_title')
  const description = restDayTemplate
    ? i18n.t('shift_management.conflict.modal_desc_2_complete')
    : i18n.t('shift_management.conflict.modal_desc_2')
  const button = restDayTemplate
    ? i18n.t('shift_management.conflict.modal_button_complete')
    : i18n.t('shift_management.conflict.modal_button')

  const labels = shiftsRowsList.map((row) => row.title).join(', ')
  const verbsCounter = getVerbCountCopy(employeeCount)
  const employeesCounter = getEmployeeCountCopy(employeeCount)

  const options = {
    count: (
      <Tooltip.Alt
        attachment="top center"
        open={tooltip}
        toggle={(toggle) => toggleTooltip(toggle)}
        label={labels}
        textAlign="center"
        inline
      >
        <Text.Normal dotted>
          {employeeCount}&nbsp;{employeesCounter}
        </Text.Normal>
      </Tooltip.Alt>
    ),

    verb: <Text.Normal>{verbsCounter}</Text.Normal>,
  }

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={
        <>
          {(submitting || submittingApply) && (
            <TextSpinner text={i18n.t('shift_management.actions.applying_rotating_shift')} />
          )}
          {!submitting &&
            !submittingApply &&
            (restDayTemplate
              ? i18n.tView('shift_management.conflict.modal_desc_complete_rotating', options)
              : i18n.tView('shift_management.conflict.modal_desc_rotating', options))}
        </>
      }
      onSubmit={onReplaceShifts}
      onClose={onClose}
      footer={
        <>
          <Button
            type="secondary"
            disabled={submitting || submittingApply}
            onClick={onApplyTemplate}
          >
            {button}
          </Button>
          <Button disabled={submitting || submittingApply} submit>
            {i18n.t('common.continue')}
          </Button>
        </>
      }
    >
      <>{!submitting && !submittingApply && <Text.Normal>{description}</Text.Normal>}</>
    </Modal>
  )
}

export default observer(OverlappingShiftsModal)
